import { DataCollectionService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Question, QuestionnaireItem } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import { camelCaseToTitleCase, capitalizeWord } from '../../portfolio-data-asset/utils';
import { emptyQuestion } from './constants';
import DataCollectionViewEditQuestion from './DataCollectionViewEditQuestion';
import { ResourceQuestionnaire } from './interfaces';

interface Props {
    activeTab: string;
    resourceQuestionnaire: ResourceQuestionnaire;
    visible: boolean;
    setVisible: (visible: boolean) => void;
    dueDate: string;
    refreshAfterCreate: () => void;
}

const dataCollectionService = new DataCollectionService();
const schemaService = new SchemaService();
function DataCollectionEditQuestionnaireModal(
    { activeTab, resourceQuestionnaire, visible, setVisible, dueDate, flash, refreshAfterCreate }: Props & FlashContextInterface
): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId ?? '';
    const dismissModal = (): void => setVisible(false);
    const isMounted = useIsMounted();

    const [ loading, setLoading ] = useState(false);
    const [ questionnaire, setQuestionnaire ] = useState<QuestionnaireItem>();
    const [ propertyOptions, setPropertyOptions ] = useState<OptionDefinition[]>([]);

    const loadPropertyOptions = async () => {
        try {
            const properties = await schemaService.listAttributes(`${activeTab}s`, portfolioId);
            if (!isMounted()) {
                return;
            }
            const propertyOptions = properties.map(property => {
                return {
                    value: property.value,
                    label: property.text ? property.text : camelCaseToTitleCase(property.value)
                };
            });
            setPropertyOptions(propertyOptions);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, constants.ERROR_LOADING_PROPERTY_OPTIONS, '');
            }
        } 
    };

    const load = async () => {
        if (!resourceQuestionnaire) {
            return;
        }
        let questionnaire: QuestionnaireItem;
        setLoading(true);
        try {
            if (resourceQuestionnaire.questionnaireId) {
                questionnaire = await dataCollectionService.get(portfolioId, resourceQuestionnaire.questionnaireId);
            } else {
                questionnaire = await dataCollectionService.create(portfolioId, 
                    {type: activeTab, resourceId: resourceQuestionnaire.resourceId, dueDate: new Date(dueDate).toUTCString()}
                );
                refreshAfterCreate();
            }
            if (isMounted()) {
                setQuestionnaire(questionnaire);
            }
        } catch (err) {
            if (isMounted()) {
                const errMsg = (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message;
                if (resourceQuestionnaire.questionnaireId) {
                    flash.alert(FlashType.error, errMsg, constants.ERROR_LOADING_QUESTIONS);
                } else {
                    flash.alert(FlashType.error, errMsg, constants.ERROR_CREATING_QUESTIONNAIRE);
                }    
                dismissModal();
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        load();
        loadPropertyOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ resourceQuestionnaire ]);

    const deleteQuestion = async (questionToDelete: Question) => {
        if (!questionnaire) {
            return;
        }
        
        const indexToRemove = questionnaire?.questions?.findIndex(question => question === questionToDelete);
        if (indexToRemove !== undefined && indexToRemove > -1) {
            if (!questionToDelete.questionId) { // question has not yet been saved
                removeQuestionAtIndex(indexToRemove);
            } else {
                try {
                    await dataCollectionService.deleteQuestion(
                        portfolioId, questionnaire.id, questionToDelete.questionId);
                    removeQuestionAtIndex(indexToRemove);
                } catch (err) {
                    // calls will wrap this function in try/catch in order to display error message next to the question
                    throw new Error(constants.ERROR_DELETING_QUESTION);
                }    
            } 
        }
    };

    const removeQuestionAtIndex = (index: number) => {
        const updatedQuestionnaire: QuestionnaireItem = deepCopy(questionnaire);
        updatedQuestionnaire.questions.splice(index, 1);
        setQuestionnaire(updatedQuestionnaire);
    };

    const updateQuestion = async (currentQuestion: Question, updatedQuestion: Question) => {
        if (!questionnaire || !updatedQuestion.questionId) {
            return;
        }
        try {
            await dataCollectionService.updateQuestion(
                portfolioId, questionnaire.id, updatedQuestion.questionId, updatedQuestion);
        } catch (err) {
            // calls will wrap this function in try/catch in order to display error message next to the question
            throw new Error(constants.ERROR_UPDATING_QUESTION); 
        }
    };

    const addQuestion = async (question: Question) => {
        if (!resourceQuestionnaire.questionnaireId) {
            return;
        }
        try {
            await dataCollectionService.addQuestion(
                portfolioId, activeTab, resourceQuestionnaire.resourceId, resourceQuestionnaire.questionnaireId,
                { answerType: question.answerType, label: question.label, propertyReference: question.propertyReference,
                    questionText: question.questionText, resourceReference: question.resourceReference, possibleAnswer: question.possibleAnswers ?? [],
                });
        } catch (err) {
            // calls will wrap this function in try/catch in order to display error message next to the question
            throw new Error(constants.ERROR_ADDING_QUESTION); 
        }
    };

    const addQuestionToQuestionnaire = () => {
        const updatedQuestionnaire: QuestionnaireItem = deepCopy(questionnaire);
        updatedQuestionnaire.questions.push(emptyQuestion);
        setQuestionnaire(updatedQuestionnaire);
    };

    const getQuestions = (): JSX.Element => {
        const questions = questionnaire?.questions?.map((question, index) => {
            return (
                <DataCollectionViewEditQuestion
                    key={index}
                    question={question}
                    activeTab={activeTab}
                    propertyOptions={propertyOptions}
                    addQuestion={addQuestion}
                    deleteQuestion={deleteQuestion}
                    updateQuestion={updateQuestion}
                />
            );
        });
        return (
            <SpaceBetween size='m'>
                {questions ?? []}
            </SpaceBetween>
        );
    };

    const footer = (
        <Box float='right'>
            <SpaceBetween size='m' direction='horizontal'>
                <Button onClick={addQuestionToQuestionnaire}>{constants.ADD_QUESTION}</Button>
                <Button onClick={dismissModal}>{constants.CLOSE_BUTTON_TEXT}</Button>
            </SpaceBetween>
        </Box>
    );

    const content = loading ? <Spinner size='large'/> : getQuestions();

    return (
        <Modal
            header={`${capitalizeWord(activeTab)} ${resourceQuestionnaire.resourceId}`}
            footer={footer}
            visible={visible}
            onDismiss={dismissModal}
            size='large'
        >
            {content}
        </Modal>
    );
}

export default withFlashContext(DataCollectionEditQuestionnaireModal);
